import React from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Stack,
  Typography,
} from "@mui/material";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import TodayRoundedIcon from "@mui/icons-material/TodayRounded";
import DateRangeRoundedIcon from "@mui/icons-material/DateRangeRounded";
import QueryStatsRoundedIcon from "@mui/icons-material/QueryStatsRounded";
import { ownerApi } from "../../../api/owner";

const usd = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 2,
  maximumFractionDigits: 6,
});

const shortDate = new Intl.DateTimeFormat("es-AR", {
  timeZone: "UTC",
  day: "2-digit",
  month: "short",
});

function formatMoney(value) {
  return usd.format(Number(value || 0));
}

function formatDateKey(value) {
  const date = new Date(`${value}T00:00:00.000Z`);
  return Number.isNaN(date.getTime()) ? value : shortDate.format(date);
}

function metricCard({ label, value, helper, icon }) {
  return (
    <Card variant="outlined" sx={{ minWidth: 0, height: "100%", borderRadius: 3 }}>
      <CardContent sx={{ p: { xs: 1.5, md: 1.8 }, "&:last-child": { pb: { xs: 1.5, md: 1.8 } } }}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: .8 }}>
          <Box sx={{ display: "grid", placeItems: "center", width: 34, height: 34, borderRadius: 2, bgcolor: "rgba(243,90,58,.09)", color: "primary.main", flex: "0 0 auto" }}>
            {icon}
          </Box>
          <Typography sx={{ color: "text.secondary", fontWeight: 900, fontSize: 12.5 }}>{label}</Typography>
        </Stack>
        <Typography sx={{ fontSize: { xs: 25, md: 29 }, lineHeight: 1.05, fontWeight: 1000, letterSpacing: "-.04em" }}>
          {formatMoney(value)}
        </Typography>
        <Typography sx={{ mt: .45, color: "text.secondary", fontSize: 11.5, fontWeight: 700 }}>{helper}</Typography>
      </CardContent>
    </Card>
  );
}

function SpendHistory({ rows = [] }) {
  const visible = rows.slice(-14).reverse();
  const max = Math.max(0, ...visible.map((row) => Number(row.amount || 0)));

  return (
    <Card variant="outlined" sx={{ borderRadius: 3, minWidth: 0 }}>
      <CardContent sx={{ p: { xs: 1.5, md: 1.8 }, "&:last-child": { pb: { xs: 1.5, md: 1.8 } } }}>
        <Typography sx={{ fontWeight: 1000, fontSize: 15 }}>Gasto diario</Typography>
        <Typography sx={{ color: "text.secondary", fontSize: 11.5, fontWeight: 700, mb: 1.25 }}>
          Últimos días informados por OpenAI · UTC
        </Typography>

        {!visible.length ? (
          <Typography sx={{ py: 2, color: "text.secondary", fontSize: 13 }}>Todavía no hay consumo informado en este período.</Typography>
        ) : (
          <Stack spacing={1}>
            {visible.map((row) => {
              const amount = Number(row.amount || 0);
              const width = max > 0 ? Math.max(2, (amount / max) * 100) : 0;
              return (
                <Box key={row.date}>
                  <Stack direction="row" justifyContent="space-between" alignItems="baseline" spacing={1}>
                    <Typography sx={{ fontSize: 12, color: "text.secondary", fontWeight: 800 }}>{formatDateKey(row.date)}</Typography>
                    <Typography sx={{ fontSize: 12.5, fontWeight: 1000 }}>{formatMoney(amount)}</Typography>
                  </Stack>
                  <Box sx={{ mt: .45, height: 7, borderRadius: 99, bgcolor: "action.hover", overflow: "hidden" }}>
                    <Box sx={{ width: `${width}%`, minWidth: amount > 0 ? 3 : 0, height: "100%", borderRadius: 99, bgcolor: "primary.main" }} />
                  </Box>
                </Box>
              );
            })}
          </Stack>
        )}
      </CardContent>
    </Card>
  );
}

function Breakdown({ rows = [] }) {
  const visible = rows.slice(0, 12);
  return (
    <Card variant="outlined" sx={{ borderRadius: 3, minWidth: 0 }}>
      <CardContent sx={{ p: { xs: 1.5, md: 1.8 }, "&:last-child": { pb: { xs: 1.5, md: 1.8 } } }}>
        <Typography sx={{ fontWeight: 1000, fontSize: 15 }}>Dónde se gastó</Typography>
        <Typography sx={{ color: "text.secondary", fontSize: 11.5, fontWeight: 700, mb: 1 }}>
          Desglose oficial por concepto de facturación
        </Typography>
        {!visible.length ? (
          <Typography sx={{ py: 2, color: "text.secondary", fontSize: 13 }}>Sin conceptos facturados todavía.</Typography>
        ) : (
          <Stack divider={<Divider flexItem />}>
            {visible.map((row) => (
              <Stack key={row.lineItem} direction="row" justifyContent="space-between" alignItems="center" spacing={1.2} sx={{ py: .9 }}>
                <Typography sx={{ minWidth: 0, overflowWrap: "anywhere", color: "text.secondary", fontSize: 11.5, fontWeight: 800 }}>{row.lineItem}</Typography>
                <Typography sx={{ whiteSpace: "nowrap", fontSize: 12.5, fontWeight: 1000 }}>{formatMoney(row.amount)}</Typography>
              </Stack>
            ))}
          </Stack>
        )}
      </CardContent>
    </Card>
  );
}

export default function OwnerAiUsagePage() {
  const [usage, setUsage] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [refreshing, setRefreshing] = React.useState(false);
  const [error, setError] = React.useState(null);

  const load = React.useCallback(async ({ silent = false } = {}) => {
    if (silent) setRefreshing(true);
    else setLoading(true);
    setError(null);
    try {
      const response = await ownerApi.openAiUsage({ days: 30 });
      setUsage(response?.usage || null);
    } catch (nextError) {
      setError(nextError);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  React.useEffect(() => {
    load();
  }, [load]);

  if (loading) {
    return (
      <Box sx={{ minHeight: 260, display: "grid", placeItems: "center" }}>
        <Stack alignItems="center" spacing={1}>
          <CircularProgress size={28} />
          <Typography sx={{ color: "text.secondary", fontSize: 12.5, fontWeight: 800 }}>Leyendo costos de OpenAI…</Typography>
        </Stack>
      </Box>
    );
  }

  const configMissing = error?.code === "OPENAI_ADMIN_KEY_MISSING";
  const totals = usage?.totals || {};

  return (
    <Box sx={{ pb: 1.5, maxWidth: 1500, mx: "auto" }}>
      <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ xs: "stretch", sm: "center" }} spacing={1} sx={{ mb: 1.2 }}>
        <Box>
          <Stack direction="row" alignItems="center" spacing={.8}>
            <AutoAwesomeRoundedIcon color="primary" fontSize="small" />
            <Typography component="h1" sx={{ fontSize: { xs: 20, md: 23 }, fontWeight: 1000, letterSpacing: "-.03em" }}>Uso de IA</Typography>
            <Chip size="small" label="Owner" sx={{ height: 22, fontWeight: 900, fontSize: 10.5 }} />
          </Stack>
          <Typography sx={{ mt: .3, color: "text.secondary", fontSize: 12.5, fontWeight: 700 }}>
            Gasto real de OpenAI API. No incluye tu suscripción personal de ChatGPT.
          </Typography>
        </Box>
        <Button variant="outlined" size="small" startIcon={refreshing ? <CircularProgress size={14} /> : <RefreshRoundedIcon />} disabled={refreshing} onClick={() => load({ silent: true })} sx={{ alignSelf: { xs: "flex-start", sm: "center" }, textTransform: "none", fontWeight: 900, borderRadius: 2.5 }}>
          Actualizar
        </Button>
      </Stack>

      {error ? (
        <Alert severity={configMissing ? "info" : "warning"} sx={{ mb: 1.2, borderRadius: 3 }}>
          <Typography sx={{ fontWeight: 900, fontSize: 13 }}>{configMissing ? "Falta conectar la lectura de costos" : "No pude leer el gasto de OpenAI"}</Typography>
          <Typography sx={{ mt: .2, fontSize: 12.5 }}>{error.message}</Typography>
          {configMissing ? (
            <Typography sx={{ mt: .7, fontSize: 12, fontWeight: 800 }}>
              Agregá OPENAI_ADMIN_KEY únicamente en el backend de Render. La OPENAI_API_KEY de Jarvis se mantiene separada.
            </Typography>
          ) : null}
        </Alert>
      ) : null}

      {usage ? (
        <>
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr 1fr", lg: "repeat(4,minmax(0,1fr))" }, gap: .9, mb: .9 }}>
            {metricCard({ label: "Este mes", value: totals.month, helper: "Mes calendario en UTC", icon: <DateRangeRoundedIcon fontSize="small" /> })}
            {metricCard({ label: "Últimos 7 días", value: totals.last7Days, helper: "Ventana móvil de 7 días", icon: <QueryStatsRoundedIcon fontSize="small" /> })}
            {metricCard({ label: "Hoy", value: totals.today, helper: "Costo informado hoy", icon: <TodayRoundedIcon fontSize="small" /> })}
            {metricCard({ label: "Promedio diario", value: totals.averagePerDayThisMonth, helper: "Promedio del mes actual", icon: <AutoAwesomeRoundedIcon fontSize="small" /> })}
          </Box>

          <Stack direction="row" alignItems="center" spacing={.8} sx={{ mb: .9, px: .15, flexWrap: "wrap", rowGap: .6 }}>
            <Chip size="small" label={usage.scope === "project" ? "Proyecto OpenAI" : "Organización OpenAI"} sx={{ fontWeight: 900, fontSize: 10.5 }} />
            <Typography sx={{ color: "text.secondary", fontSize: 11.5, fontWeight: 700 }}>
              Última lectura: {usage.generatedAt ? new Date(usage.generatedAt).toLocaleString("es-AR") : "—"}
            </Typography>
          </Stack>

          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "minmax(0,1.15fr) minmax(320px,.85fr)" }, gap: .9 }}>
            <SpendHistory rows={usage.daily} />
            <Breakdown rows={usage.breakdown} />
          </Box>
        </>
      ) : null}
    </Box>
  );
}
